import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import {
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';

import { PhoneLink } from './PhoneLink';
import { formatOrderDeliveryPreference } from '../utils/orderDeliveryPreference';
import type { ClinicPhysicianResponse } from '../services/api/types.gen';

interface PhysicianInfoPopoverProps {
  /**
   * The physician whose details to show, or null to render nothing.
   *
   * Takes the whole roster object rather than an id because every caller so far
   * already holds one: the clinic roster is fetched as a unit and this component
   * has no way to fetch on its own. A caller holding only an NPI or an id will
   * need to look it up in that roster first, or widen this prop.
   */
  physician: ClinicPhysicianResponse | null;
  className?: string;
}

/**
 * An info icon beside a physician picker that opens the selected physician's
 * contact and billing metadata.
 *
 * Click/tap rather than hover, so it works the same on touch and pointer
 * devices. Rows for fields the physician has no value for are omitted rather
 * than shown with a placeholder, which keeps the card short on a phone.
 */
export default function PhysicianInfoPopover({
  physician,
  className,
}: PhysicianInfoPopoverProps) {
  // No physician, or one that is no longer on the clinic roster, means there is
  // nothing to show. Render nothing at all rather than a dead icon.
  if (!physician) return null;

  // `wrap` opts a row out of single-line truncation. Only the address needs it:
  // a street address is the one value that is legitimately multi-line, and
  // clipping it to one line hides the part that identifies the location.
  const rows: { label: string; value: ReactNode; wrap?: boolean }[] = [];

  if (physician.npi) rows.push({ label: 'NPI', value: physician.npi });
  if (physician.phone)
    rows.push({ label: 'Phone', value: <PhoneLink phone={physician.phone} /> });
  if (physician.fax)
    rows.push({ label: 'Fax', value: <PhoneLink phone={physician.fax} /> });
  if (physician.email)
    rows.push({
      label: 'Email',
      value: (
        <a
          href={`mailto:${physician.email}`}
          className='text-blue-600 hover:underline'
        >
          {physician.email}
        </a>
      ),
    });
  rows.push({
    label: 'Delivery',
    value: formatOrderDeliveryPreference(physician.order_delivery_preference),
  });
  if (physician.address)
    rows.push({ label: 'Address', value: physician.address, wrap: true });
  if (physician.pecos != null)
    rows.push({ label: 'PECOS', value: physician.pecos ? 'Yes' : 'No' });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label='Physician details'
          // p-1.5 gives a 26px tap target, comfortably above the 24px
          // minimum for a thumb. The caller is expected to bound the row
          // height, so the padding does not push neighbouring layout around.
          className={cn(
            'rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
          )}
        >
          <Info className='h-3.5 w-3.5' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-0' side='bottom' align='start'>
        <div className='border-b px-3 py-2'>
          <p className='text-xs font-medium text-muted-foreground'>
            Physician Details
          </p>
        </div>
        <dl className='space-y-1 px-3 py-2'>
          {rows.map(({ label, value, wrap }) => (
            <div key={label} className='flex justify-between gap-2 text-xs'>
              <dt className='shrink-0 text-muted-foreground'>{label}</dt>
              <dd
                className={cn(
                  'min-w-0 text-right font-medium',
                  wrap ? 'whitespace-pre-line break-words' : 'truncate'
                )}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}
